import * as React from 'react';
import { Button, Input, message, Spin, Row, Col, Image, Typography } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { sendLoginOTP } from '@api/login';
import withStyles from 'react-jss';
import styles from '@components/pages/SubmitOtp/SubmitOtp.styles';
const shipsyLogoMain = require('../../../assets/images/ShipsyLogo.png');
import { useTranslation } from 'react-i18next';

function SubmitOtpPage(props: any) {
  const {
    username,
    otpUnblockDuration = 30,
    otpUnblockTime,
    organisationDetails,
    urlConfigs,
    onBack,
    onSubmitOtp,
  } = props;
  const { classes } = props;
  const { t } = useTranslation();
  const [isSubmitOtpLoading, setIsSubmitOtpLoading] = React.useState(false);
  const [otp, setOtp] = React.useState('');
  const [resendTimer, setResendTimer] = React.useState(0);
  const [canResend, setCanResend] = React.useState(true);
  const [
    currentOtpUnblockDuration, setCurrentOtpUnblockDuration
  ] = React.useState(otpUnblockDuration);

  React.useEffect(() => {
    if (otpUnblockTime) {
      const unblockTime = new Date(otpUnblockTime);
      const now = new Date();
      const remainingSeconds = Math.max(
        0, Math.floor((unblockTime.getTime() - now.getTime()) / 1000));
      if (remainingSeconds > 0) {
        setResendTimer(remainingSeconds);
        setCanResend(false);
      }
    }
  }, [otpUnblockTime]);

  React.useEffect(() => {
    let interval: NodeJS.Timeout;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const onChangeOtp = (e) => {
    setOtp(e.target.value);
  };

  const onClickSubmitOTP = async (e) => {
    if (!otp) {
      message.error(t('Please enter OTP'));
      return;
    }

    if (!username) {
      message.error(t('Session expired. Please login again.'));
      if (onBack) onBack();
      return;
    }

    setIsSubmitOtpLoading(true);

    if (onSubmitOtp) {
      await onSubmitOtp(e, otp);
    }

    setIsSubmitOtpLoading(false);
  };

  const onClickResendOTP = async () => {
    if (!canResend) return;

    if (!username) {
      message.error(t('Session expired. Please login again.'));
      if (onBack) onBack();
      return;
    }

    setCanResend(false);
    const reqHeaders = {
      'Content-Type': 'application/json;charset=UTF-8',
      'organisation-pretty-name': urlConfigs.organisationPrettyName,
      'organisation-url': window.location.hostname,
    };
    const reqBody = { username };

    const response = await sendLoginOTP(reqHeaders, reqBody);
    if (response.isSuccess) {
      message.success(t('OTP sent successfully'));
      const newOtpUnblockDuration = response.data?.otp_unblock_duration
        || currentOtpUnblockDuration;
      setCurrentOtpUnblockDuration(newOtpUnblockDuration);
      setResendTimer(newOtpUnblockDuration);
    } else {
      message.error(response.errorMessage);
      setCanResend(true);
    }
  };

  const SubmitOtpFormBody = () => {
    return (
      <form onSubmit={(e) => { onClickSubmitOTP(e); }} className={classes.loginContainer} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', flexGrow: 1 }}>
        <div>
          <img
            className={classes.logoImage}
            src={organisationDetails.logo_url || shipsyLogoMain}
          />
        </div>
        <h2 className={classes.heading}>{t('2FA Verification')}</h2>
        <div className={classes.subText}>
          <p>{t('Please enter the OTP sent to your registered email')}</p>
        </div>
        <div className={classes.inputBox}>
          <Input
            placeholder={t('Enter OTP')}
            onChange={onChangeOtp}
            value={otp}
            maxLength={6}
          />
        </div>
        <div className={classes.submitButtonContainer}>
          <Button
            className={classes.submitButton}
            type="primary"
            onClick={onClickSubmitOTP}
            disabled={!otp || otp?.length === 0 || isSubmitOtpLoading}
            block
          >
            {isSubmitOtpLoading ?
              <Spin indicator={
                <LoadingOutlined style={{ fontSize: 16, color: '#fff' }} spin />
              } />
              : t('Verify OTP')}
          </Button>
        </div>
        <div className={classes.forgotPasswordBtnDiv}>
          <Typography.Text
            onClick={onClickResendOTP}
            disabled={!canResend}
            style={{ opacity: canResend ? 1 : 0.5, cursor: canResend ? 'pointer' : 'not-allowed' }}
          >
            {canResend ? t('Resend OTP') : `${t('Resend OTP in')} ${resendTimer}s`}
          </Typography.Text>
        </div>
      </form>
    );
  };

  return (
    <Row
      className={classes.roundedAndShadowBorder}
      style={{ background: '#EFF5FB' }}
    >
      {SubmitOtpFormBody()}
    </Row>
  );
}

export default withStyles(styles, { injectTheme: true })(SubmitOtpPage);
